"use client";

import React, { useMemo } from "react";
import Image from "next/image";
import SmoothImage from "@/components/SmoothImage";
import Link from "next/link";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import { Calendar } from "lucide-react";
import { useFetchNewsArticles } from "@/apis/queryFunctions/news";
import { dictionaries, Locale } from "@/utils/i18n";
import ScrollHeader from "@/components/Layouts/ScrollHeader";
import FooterContact from "@/components/Layouts/FooterContact";
import { PROJECT_INFO } from "@/types/projectConsts";
import { stripHTML, parseHtmlTextToJson } from "@/utils/stripHTML";
import footerContactData from "@/data/footerContactData.json";

interface TinTucClientProps {
  homepageData: any;
  locale: string;
}

const formatDate = (dateString: string) => {
  if (!dateString) return "";
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (e) {
    return dateString;
  }
};

export default function TinTucClient({
  homepageData,
  locale,
}: TinTucClientProps) {
  // Fetch live articles list from query
  const { data: newsItems = [], isLoading } = useFetchNewsArticles({
    condition: { ngon_ngu: locale },
  });

  const dict =
    dictionaries[locale as Locale]?.news_flow || dictionaries.vi.news_flow;

  // Flatten add-on list
  const allAddOns = useMemo(
    () => [
      ...(Array.isArray(homepageData?.add_on_data)
        ? homepageData.add_on_data
        : []),
    ],
    [homepageData?.add_on_data],
  );

  const getAddOn = (key: string) => {
    return (
      allAddOns.find(
        (item: any) => item?.title?.toLowerCase() === key.toLowerCase(),
      ) || null
    );
  };

  const getAddOn2 = (key: string) => {
    return (
      homepageData?.add_on_data_2?.find(
        (item: any) => item?.title?.toLowerCase() === key.toLowerCase(),
      ) || null
    );
  };

  // Dynamically configure global PROJECT_INFO properties from project_info JSON API data
  const projectInfo = homepageData?.content;
  if (projectInfo) {
    try {
      const cleanContent = stripHTML(projectInfo).trim();
      const parsed = JSON.parse(cleanContent);
      if (parsed.projectName) PROJECT_INFO.projectName = parsed.projectName;
      if (parsed.address) PROJECT_INFO.address = parsed.address;
      if (parsed.hotline) PROJECT_INFO.hotline = parsed.hotline;
      if (parsed.email) PROJECT_INFO.email = parsed.email;
      if (parsed.website) PROJECT_INFO.website = parsed.website;
      if (parsed.zalo) PROJECT_INFO.zalo = parsed.zalo;
      if (parsed.messenger) {
        PROJECT_INFO.messenger = parsed.messenger;
        PROJECT_INFO.fb = parsed.messenger;
      } else if (parsed.fb) {
        PROJECT_INFO.messenger = parsed.fb;
        PROJECT_INFO.fb = parsed.fb;
      }
    } catch (e) {
      console.error("Failed to parse project_info JSON:", e);
    }
  }

  // Header options mappings
  const heroItem = getAddOn("herosection");
  let heroFormTitle = "ĐĂNG KÝ TƯ VẤN NGAY";
  let heroFormSubtitle = "Nhận thông tin mặt bằng & bảng giá mới nhất";
  let heroFormLayoutTypeTitle = "Sản phẩm quan tâm";
  let heroApptOptions: Array<{ label: string; value: string }> = [
    { label: "1PN", value: "1PN" },
    { label: "2PN", value: "2PN" },
    { label: "Studio", value: "Studio" },
  ];

  try {
    const rawContent = heroItem?.content
      ? stripHTML(heroItem.content).trim()
      : "";
    if (rawContent) {
      const parsed = JSON.parse(rawContent);
      if (parsed.heroFormTitle) heroFormTitle = parsed.heroFormTitle;
      if (parsed.heroFormSubtitle) heroFormSubtitle = parsed.heroFormSubtitle;
      if (parsed.heroFormLayoutTypeTitle)
        heroFormLayoutTypeTitle = parsed.heroFormLayoutTypeTitle;
      if (parsed.heroApptOptions && Array.isArray(parsed.heroApptOptions)) {
        heroApptOptions = parsed.heroApptOptions.map((item: any) => ({
          label: item.label || item.title || "Căn hộ",
          value: item.value || item.title || "Studio",
        }));
      }
    }
  } catch (e) {
    console.error("Failed to parse hero options:", e);
  }

  // Dynamic Footer Data construction
  const footerItem = getAddOn("footer");

  const dynamicFooterContactData = useMemo(() => {
    let baseData: any = {};
    if (footerItem?.content) {
      const parsed = parseHtmlTextToJson<any>(footerItem.content);
      baseData = Array.isArray(parsed) ? parsed[0] || {} : parsed || {};
    }

    const projectLogoObj =
      baseData.projectLogo || footerContactData.middleColumn.projectLogo;
    const projectLogoSrc = footerItem?.picture
      ? appendImageUrlFromAPI({ src: footerItem.picture, size: "xl" })
      : "";

    const dev1Src = footerItem?.picture_2
      ? appendImageUrlFromAPI({ src: footerItem.picture_2, size: "xl" })
      : "";
    const dev2Src = footerItem?.picture_3
      ? appendImageUrlFromAPI({ src: footerItem.picture_3, size: "xl" })
      : "";

    const rawDevs =
      baseData.developers || footerContactData.middleColumn.developers || [];
    const developers = rawDevs.map((dev: any, idx: number) => ({
      role: dev.role || (idx === 0 ? "CHỦ ĐẦU TƯ" : "NHÀ PHÁT TRIỂN"),
      name: dev.name || "",
      src:
        (idx === 0
          ? dev1Src || dev.src
          : idx === 1
            ? dev2Src || dev.src
            : dev.src) || "",
    }));

    const partnerRecord = getAddOn2("footer-partners");

    let partnerJson: any = {};
    if (partnerRecord?.content) {
      const parsed = parseHtmlTextToJson<any>(partnerRecord.content);
      partnerJson = Array.isArray(parsed) ? parsed[0] || {} : parsed || {};
    }

    const partnerLogos = [
      partnerRecord?.picture,
      partnerRecord?.picture_2,
      partnerRecord?.picture_3,
      partnerRecord?.picture_4,
      partnerRecord?.picture_5,
      partnerRecord?.picture_6,
    ]
      .filter(Boolean)
      .map((src) => appendImageUrlFromAPI({ src, size: "s" }));

    const rawPartners =
      partnerJson.partners || footerContactData.middleColumn.partners || [];
    const partners = rawPartners.map((p: any, idx: number) => ({
      name: p.name || `Đối tác ${idx + 1}`,
      src: partnerLogos[idx] || p.src || "",
    }));

    return {
      tag: baseData.tag || footerContactData.tag,
      title: baseData.title || footerContactData.title,
      description: baseData.description || footerContactData.description,
      hotlineLabel: baseData.hotlineLabel || footerContactData.hotlineLabel,
      hotlineValue: baseData.hotlineValue || footerContactData.hotlineValue,
      emailLabel: baseData.emailLabel || footerContactData.emailLabel,
      emailValue: baseData.emailValue || footerContactData.emailValue,
      middleColumn: {
        developersTitle:
          baseData.developersTitle ||
          footerContactData.middleColumn.developersTitle,
        projectLogo: {
          name: projectLogoObj.name || "DRAGON HOME",
          src: projectLogoSrc,
        },
        developers,
        partnersTitle:
          partnerJson.partnersTitle ||
          footerContactData.middleColumn.partnersTitle,
        partners,
      },
      formTitle: baseData.formTitle || footerContactData.formTitle,
      formSecurityNote:
        baseData.formSecurityNote || footerContactData.formSecurityNote,
      formSuccessTitle:
        baseData.formSuccessTitle || footerContactData.formSuccessTitle,
      formSuccessDesc:
        baseData.formSuccessDesc || footerContactData.formSuccessDesc,
      nameLabel: baseData.nameLabel || footerContactData.nameLabel,
      namePlaceholder:
        baseData.namePlaceholder || footerContactData.namePlaceholder,
      phoneLabel: baseData.phoneLabel || footerContactData.phoneLabel,
      phonePlaceholder:
        baseData.phonePlaceholder || footerContactData.phonePlaceholder,
      needLabel: baseData.needLabel || footerContactData.needLabel,
      needOptions: baseData.needOptions || footerContactData.needOptions,
      formFooterNote:
        baseData.formFooterNote || footerContactData.formFooterNote,
      submitBtnText: baseData.submitBtnText || footerContactData.submitBtnText,
      submittingBtnText:
        baseData.submittingBtnText || footerContactData.submittingBtnText,
    };
  }, [footerItem, homepageData?.add_on_data_2]);

  const featuredArticle = newsItems.length > 0 ? newsItems[0] : null;
  const remainingArticles =
    newsItems.length > 1 ? newsItems.slice(1) : newsItems;

  return (
    <>
      <ScrollHeader
        apptOptions={heroApptOptions}
        formTitle={heroFormTitle}
        formSubtitle={heroFormSubtitle}
        formLayoutTypeTitle={heroFormLayoutTypeTitle}
      />

      <main className="min-h-screen  text-[#193B2F]  relative">
        {/* HERO BANNER SECTION - BRAND GREEN STYLE */}
        <section className="relative py-20 md:py-28 text-white text-center overflow-hidden bg-primary border-b border-[#193B2F]/20 select-none">
          {/* BACKGROUND TEXTURE OVERLAY */}
          <div className="absolute inset-0 z-0 pointer-events-none mix-blend-multiply opacity-40">
            <Image
              src="/images/phoi_canh_3.png"
              alt="texture"
              fill
              sizes="100vw"
              priority
              className="object-cover object-center"
            />
          </div>

          <div className="relative z-10 max-w-4xl mx-auto px-6 space-y-3">
            <div className="flex items-center justify-center gap-3">
              <span className="w-7 h-[1px] bg-[#E5D4B2]" />
              <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.22em] text-[#E5D4B2] font-sans">
                DRAGON HOME NEWS & EVENTS
              </span>
              <span className="w-7 h-[1px] bg-[#E5D4B2]" />
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-lora font-normal uppercase tracking-tight text-white">
              {dict.blog_title || "TIN TỨC & SỰ KIỆN"}
            </h1>

            <p className="text-xs sm:text-sm md:text-base text-white/80 font-sans font-light max-w-2xl mx-auto leading-relaxed">
              {dict.blog_desc ||
                "Cập nhật các thông tin, sự kiện và tiến độ dự án mới nhất"}
            </p>
          </div>
        </section>

        {/* CONTENT SECTION WITH FULL-WIDTH PAPER TEXTURE */}
        <section className="relative w-full overflow-hidden bg-[#FAF8F5]">
          {/* PAPER TEXTURE OVERLAY - FULL WIDTH */}
          <div className="absolute inset-0 z-0 pointer-events-none mix-blend-multiply opacity-50">
            <Image
              src="/images/background_2.png"
              alt="texture"
              fill
              sizes="100vw"
              priority
              className="object-cover object-center"
            />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 py-16 md:py-20">
            {isLoading ? (
              <div className="flex justify-center items-center py-24">
                <div className="w-10 h-10 border-4 border-[#C9AB81] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : newsItems.length === 0 ? (
              <div className="text-center py-20 text-[#193B2F]/70 font-sans font-semibold text-sm">
                {dict.no_results || "Không tìm thấy bài viết nào."}
              </div>
            ) : (
              <div className="space-y-12">
                {/* FEATURED ARTICLE HIGHLIGHT */}
                {featuredArticle && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="w-7 h-[1px] bg-[#8A724C]" />
                      <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8A724C] font-sans">
                        BÀI VIẾT NỔI BẬT
                      </span>
                    </div>

                    <Link
                      href={`/${locale}/tin-tuc/${featuredArticle.slug}`}
                      className="grid grid-cols-1 lg:grid-cols-12 bg-[#FAF8F5] border border-[#193B2F]/15 hover:border-[#C9AB81] shadow-sm hover:shadow-xl transition-all duration-300 group cursor-pointer overflow-hidden"
                    >
                      <div className="lg:col-span-7 relative aspect-[16/10] lg:aspect-auto min-h-[280px] lg:min-h-[380px] overflow-hidden bg-[#EAE5DB]">
                        <SmoothImage
                          src={
                            appendImageUrlFromAPI({
                              src:
                                featuredArticle.picture ||
                                featuredArticle.image,
                              size: "2k",
                            }) || "/images/map_ora.png"
                          }
                          alt={featuredArticle.title}
                          fill
                          sizes="(min-width: 1024px) 60vw, 100vw"
                          className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                        />
                        {featuredArticle.loai_tin_tuc && (
                          <span className="absolute top-4 left-4 bg-[#E5D4B2] text-[#193B2F] font-sans font-bold text-xs uppercase tracking-wider px-3.5 py-1 shadow-sm">
                            {featuredArticle.loai_tin_tuc}
                          </span>
                        )}
                      </div>
                      <div className="lg:col-span-5 p-7 md:p-9 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center gap-1.5 text-[#8A724C] font-sans text-xs font-semibold uppercase tracking-wider mb-3">
                            <Calendar size={13} />
                            <span>
                              {formatDate(
                                featuredArticle.date_created ||
                                  featuredArticle.date,
                              )}
                            </span>
                          </div>
                          <h2 className="font-lora text-xl md:text-2xl font-normal text-[#193B2F] leading-snug mb-3 group-hover:text-[#8A724C] transition-colors">
                            {featuredArticle.title}
                          </h2>
                          <p className="font-sans text-xs sm:text-sm text-[#193B2F]/75 line-clamp-4 leading-relaxed">
                            {featuredArticle.summary ||
                              (featuredArticle.content
                                ? featuredArticle.content
                                    .replace(/<[^>]*>/g, "")
                                    .substring(0, 180) + "..."
                                : "")}
                          </p>
                        </div>
                        <div className="pt-6 border-t border-[#193B2F]/10 mt-6 flex items-center justify-between">
                          <span className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#E5D4B2] group-hover:bg-[#C9AB81] text-[#193B2F] border border-[#C9AB81] font-sans text-xs font-bold uppercase tracking-wider transition-all duration-300 shadow-sm">
                            <span>{dict.read_more || "Đọc chi tiết"}</span>
                            <svg
                              className="w-3.5 h-3.5 fill-current"
                              viewBox="0 0 24 24"
                            >
                              <path
                                d="M5 12h14M12 5l7 7-7 7"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                fill="none"
                              />
                            </svg>
                          </span>
                        </div>
                      </div>
                    </Link>
                  </div>
                )}

                {/* REMAINING ARTICLES GRID */}
                {remainingArticles.length > 0 && (
                  <div>
                    {featuredArticle && (
                      <div className="flex items-center gap-3 mb-6 pt-4">
                        <span className="w-7 h-[1px] bg-[#8A724C]" />
                        <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8A724C] font-sans">
                          TẤT CẢ BÀI VIẾT
                        </span>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                      {remainingArticles.map((item: any) => {
                        const imageUrl = appendImageUrlFromAPI({
                          src: item.picture || item.image,
                          size: "xl",
                        });
                        return (
                          <Link
                            key={item._id || item.id}
                            href={`/${locale}/tin-tuc/${item.slug}`}
                            className="flex flex-col justify-between bg-[#FAF8F5] border border-[#193B2F]/10 hover:border-[#C9AB81] shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer group h-full"
                          >
                            <div>
                              <div className="relative aspect-[16/10] bg-[#EAE5DB] overflow-hidden">
                                <SmoothImage
                                  src={imageUrl || "/images/map_ora.png"}
                                  alt={item.title}
                                  fill
                                  sizes="(min-width: 1024px) 30vw, (min-width: 768px) 50vw, 100vw"
                                  className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                />
                                {item.loai_tin_tuc && (
                                  <span className="absolute top-3 left-3 bg-[#E5D4B2] text-[#193B2F] font-sans font-bold text-[10px] uppercase tracking-wider px-2.5 py-1 shadow-sm">
                                    {item.loai_tin_tuc}
                                  </span>
                                )}
                              </div>

                              <div className="p-5 sm:p-6 flex flex-col flex-1">
                                <div className="flex items-center gap-1.5 text-[#8A724C] font-sans text-[11px] font-semibold uppercase tracking-wider mb-2">
                                  <Calendar size={12} />
                                  <span>
                                    {formatDate(item.date_created || item.date)}
                                  </span>
                                </div>

                                <h3 className="font-lora text-lg sm:text-xl text-[#193B2F] font-normal leading-snug group-hover:text-[#8A724C] transition-colors line-clamp-2 mb-2">
                                  {item.title}
                                </h3>

                                <p className="font-sans text-xs text-[#193B2F]/70 line-clamp-3 leading-relaxed">
                                  {item.summary ||
                                    (item.content
                                      ? item.content
                                          .replace(/<[^>]*>/g, "")
                                          .substring(0, 120) + "..."
                                      : "")}
                                </p>
                              </div>
                            </div>

                            <div className="px-5 sm:px-6 pb-5 sm:pb-6 pt-3 border-t border-[#193B2F]/10 flex items-center justify-between">
                              <span className="text-[#8A724C] font-sans font-bold text-xs uppercase tracking-wider group-hover:translate-x-1.5 transition-transform duration-300 inline-flex items-center gap-1.5">
                                <span>{dict.read_more || "Đọc chi tiết"}</span>
                                <svg
                                  className="w-3.5 h-3.5 fill-current"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    d="M5 12h14M12 5l7 7-7 7"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    fill="none"
                                  />
                                </svg>
                              </span>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </main>

      <div id="dang-ky-bao-gia">
        <FooterContact data={dynamicFooterContactData} />
      </div>
    </>
  );
}
